import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CaretDown} from '../../../icons';
import {kindLabelKey} from '../../memory/contextPackConstants';
import {anchorAttr} from '../../../../guides/anchors';

/**
 * Los seis materiales con los que arranca una memoria, mapeados a los `document_kind` que YA
 * existen (`core/services/context_pack.py` → `memory/contextPackConstants.js`).
 *
 * No se inventa una taxonomía paralela: el tipo que se declara acá es el mismo con el que el
 * Paquete de Contexto marca el ítem como presente, así que subir un brief desde esta guía lo
 * registra como «Brief de alta o kickoff» del checklist. Un enum propio dejaría las dos pantallas
 * contando cosas distintas con las mismas palabras.
 *
 * `MEETING_TRANSCRIPT` es el raro y por eso lleva `meeting: true`: el backend NO lo acepta como
 * `document_kind` declarado al crear (`_DECLARABLE_KINDS_ON_CREATE` lo excluye) porque su tipo lo
 * fija el `source=MEETING` del formulario de reunión. Preseleccionarlo sería armar un 400 seguro;
 * su vía es el flujo de carga de reunión.
 */
export const STARTER_ITEMS = [
    {kind: 'KICKOFF_BRIEF'},
    {kind: 'BUYER_PERSONA'},
    {kind: 'MARKETING_PLAN'},
    {kind: 'SCOPE_AGREEMENT'},
    {kind: 'BRAND_GUIDELINES'},
    {kind: 'MEETING_TRANSCRIPT', meeting: true},
];

export const guideDescKey = (kind) => `ia_booster.feed.guide_desc_${kind}`;

/**
 * «¿No sabes por dónde empezar?» — el instructivo colapsable del acto Alimenta.
 *
 * Existe porque la Barra Total no dice QUÉ sirve: una caja de texto vacía frente a un cliente nuevo
 * es la razón número uno por la que una memoria se queda en cero. Cada tarjeta nombra un material
 * concreto y deja la Barra Total lista para pegarlo, con el tipo ya elegido y el placeholder de ese
 * tipo.
 *
 * ⚠️ **Seis tarjetas planas, sin conteo y sin estado** (mockup: `PREFILL` + `toggleGuide`). Entre el
 * 03/09 y el 08/09 la guía supo qué material ya estaba cargado y lo decía: «Faltan 5 · Ver cuáles»,
 * «El material base ya está cargado», los faltantes primero y el tachado de lo cargado. Se sacó
 * entero (card ScPxmmB3, la pasada de Pablo contra el mockup): **son recomendaciones, no una
 * tarea**, y un contador de faltantes las convierte en deuda —«te da la sensación de que tenés que
 * hacer algo que en realidad no es obligatorio»— justo en la superficie de ACTIVACIÓN. La
 * completitud sigue viviendo donde corresponde: el strip del panel clásico de la Memoria, que abre
 * quien ya decidió ir a completar el checklist.
 *
 * Del checklist sobrevive sólo lo que NO cuenta nada: qué material se le puede pedir al cliente
 * (`requestableMissing`, que dibuja el botón de abajo) y si la memoria está en CERO, que decide si
 * la guía se abre sola.
 *
 * Va colapsada por default, salvo en frío (nada cargado todavía). Desde la card wugYmCtZ vive ARRIBA
 * del orbe (`FeedGuideBlock`), así que abierta en frío empuja la carga hacia abajo: es a propósito,
 * con la memoria en cero lo primero que hay que leer es qué cargar.
 *
 * Debajo del rótulo lleva, siempre visible, POR QUÉ importa lo que se carga (`guide_why`): para la
 * agencia que no es técnica la pregunta previa a «qué material» es «¿para qué?» (Leo, Bakián, 07/10).
 * La IA del equipo responde con este contexto, y la del cliente en el portal con lo que se le
 * comparte (un hecho nace sólo para la agencia, `ClientFact._visibility`).
 */
export function FeedStarterGuide({
    clientId, onPick, summary = null, onRequestMaterial = null, kicker = null,
}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const autoOpened = useRef(false);

    // Frío = el checklist cargó y no hay NADA. Es la única lectura que se le hace al resumen para
    // dibujar la guía, y no se muestra en ningún lado: decide si se abre sola y nada más. Una única
    // vez (el ref evita pelearle al usuario que la cerró, y que un refresh del pack la reabra).
    const isCold = !!(summary && summary.items && summary.items.length
        && summary.items.every((item) => !item.present));
    useEffect(() => {
        if (isCold && !autoOpened.current) {
            autoOpened.current = true;
            setOpen(true);
        }
    }, [isCold]);

    const requestable = (summary && summary.requestableMissing) || [];

    return (
        <div className={`iab-guide ${open ? '-open' : ''}`}>
            {/* La fila del mockup (`.guiderow`): el rótulo del acto a la izquierda y el instructivo
                a la derecha, cerrando el bloque de la carga con una línea. */}
            <div className="iab-guide-row">
                {kicker}
                <button type="button" className="iab-guide-toggle"
                        aria-expanded={open}
                        onClick={() => setOpen(value => !value)}
                        {...anchorAttr('booster_feed_guide')}>
                    {t('ia_booster.feed.guide_toggle')}
                    {' '}
                    <b>{t('ia_booster.feed.guide_toggle_em')}</b>
                    <span className="iab-guide-caret" aria-hidden="true">
                        <CaretDown size={12} weight="bold"/>
                    </span>
                </button>
            </div>
            <p className="iab-guide-why">{t('ia_booster.feed.guide_why')}</p>

            {open && (
                <div className="iab-guide-wrap">
                    <div className="iab-guide-cards">
                        {STARTER_ITEMS.map((item) => {
                            const label = t(kindLabelKey(item.kind));
                            const desc = t(guideDescKey(item.kind));
                            return (
                                <button key={item.kind} type="button" className="iab-guide-card"
                                        onClick={() => onPick && onPick(item.kind)}>
                                    <b>{label}</b>
                                    <span>{desc}</span>
                                </button>
                            );
                        })}
                    </div>
                    {/* El mockup ofrece acá «también sirven audios (los transcribimos)». NO existe
                        transcripción de audio en la plataforma: prometerla es que el usuario suba un
                        .mp3, reciba un rechazo y pierda la confianza en todo lo demás que dice esta
                        pantalla. Lo que sí entra es el texto ya transcripto. */}
                    <p className="iab-guide-note">{t('ia_booster.feed.guide_note')}</p>
                    {/* Pedirle el material al cliente: lo que falta muchas veces no lo tiene la
                        agencia. En el marco clásico esto es un botón del checklist; en 2.1 vivía
                        sólo dentro del strip del panel lateral, o sea escondido detrás de dos clics
                        justo para quien más lo necesita. Es una ACCIÓN, no un conteo: no dice
                        cuántos faltan ni cuáles. */}
                    {onRequestMaterial && requestable.length > 0 && (
                        <button type="button" className="iab-guide-request"
                                onClick={onRequestMaterial}>
                            {t('client_memory.context_pack.request_button')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default FeedStarterGuide;
